/**
 * Mock Data for FranchiseOS Dashboard
 *
 * All mock/demo data is centralized here for easy future replacement
 * with real API calls. Each function is clearly named and documented.
 */

import {
  AUVTrendPoint,
  OnboardingEntry,
  ARAgingEntry,
  LeadFunnelStage,
  LedgerSyncData,
} from '../../types/franchise';

// ─── Zone 1: Vital Signs ────────────────────────────────────────

type MockBrandKey = 'AutoFix' | 'CleanCo' | 'LawnPros' | 'PetCare';

const MOCK_BRAND_KEYS: MockBrandKey[] = ['AutoFix', 'CleanCo', 'LawnPros', 'PetCare'];

const normalizeBrandName = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Maps arbitrary brand labels to the canonical mock-data keys.
 * Handles case/spacing/punctuation differences plus prefix/suffix additions.
 */
export const resolveMockBrandKey = (brandName?: string): MockBrandKey | undefined => {
  if (!brandName) {
    return undefined;
  }

  const exactMatch = MOCK_BRAND_KEYS.find((key) => key === brandName);
  if (exactMatch) {
    return exactMatch;
  }

  const normalizedInput = normalizeBrandName(brandName);
  if (!normalizedInput) {
    return undefined;
  }

  const normalizedExactMatch = MOCK_BRAND_KEYS.find(
    (key) => normalizeBrandName(key) === normalizedInput,
  );
  if (normalizedExactMatch) {
    return normalizedExactMatch;
  }

  return MOCK_BRAND_KEYS.find((key) => {
    const normalizedKey = normalizeBrandName(key);
    return normalizedInput.includes(normalizedKey) || normalizedKey.includes(normalizedInput);
  });
};

const getMockValueForBrand = <T>(
  brandName: string | undefined,
  valuesByBrand: Record<MockBrandKey, T>,
  enterpriseFallback: T,
): T => {
  const brandKey = resolveMockBrandKey(brandName);
  return brandKey ? valuesByBrand[brandKey] : enterpriseFallback;
};

const BRAND_SALES: Record<MockBrandKey, { revenue: number; royaltyScore: number }> = {
  AutoFix:  { revenue: 1850000, royaltyScore: 94 },
  CleanCo:  { revenue: 1120000, royaltyScore: 99 },
  LawnPros: { revenue:  890000, royaltyScore: 97 },
  PetCare:  { revenue:  460000, royaltyScore: 95 },
};

/**
 * Returns mock sales and royalty integrity data for a brand.
 * Used as fallback when real API revenue data is insufficient.
 */
export const getMockBrandSales = (brandName?: string): { revenue: number; royaltyScore: number } => {
  return getMockValueForBrand(brandName, BRAND_SALES, {
    // Enterprise total = sum of all brands: 1,850,000 + 1,120,000 + 890,000 + 460,000
    revenue: 4320000,
    royaltyScore: 97,
  });
};

const BRAND_SWS_DELTA: Record<MockBrandKey, { value: number; direction: 'up' | 'down' | 'stable' }> = {
  AutoFix:  { value: 2.1, direction: 'up' },
  CleanCo:  { value: 4.5, direction: 'up' },
  LawnPros: { value: 3.8, direction: 'up' },
  PetCare:  { value: 1.2, direction: 'stable' },
};

/**
 * Returns mock "vs last month" delta for System-Wide Sales.
 * Pass a brand name to get brand-specific data.
 */
export const getMockSWSDelta = (brandName?: string): { value: number; direction: 'up' | 'down' | 'stable' } =>
  getMockValueForBrand(brandName, BRAND_SWS_DELTA, { value: 3.1, direction: 'up' });

const BRAND_WORK_ORDERS: Record<MockBrandKey, { count: number; delta: number; direction: 'up' | 'down' | 'stable' }> = {
  AutoFix:  { count: 38, delta: 3, direction: 'up' },
  CleanCo:  { count: 44, delta: 5, direction: 'up' },
  LawnPros: { count: 29, delta: 2, direction: 'up' },
  PetCare:  { count: 16, delta: 1, direction: 'stable' },
};

/**
 * Returns mock Active Work Orders count.
 * Pass a brand name to get brand-specific data.
 * In production, this would come from a jobs/work-orders endpoint.
 */
export const getMockActiveWorkOrders = (brandName?: string): { count: number; delta: number; direction: 'up' | 'down' | 'stable' } =>
  getMockValueForBrand(brandName, BRAND_WORK_ORDERS, { count: 127, delta: 8, direction: 'up' });

const BRAND_NPS: Record<MockBrandKey, { score: number; delta: number; direction: 'up' | 'down' | 'stable' }> = {
  AutoFix:  { score: 68, delta: 1, direction: 'up' },
  CleanCo:  { score: 78, delta: 3, direction: 'up' },
  LawnPros: { score: 74, delta: 2, direction: 'up' },
  PetCare:  { score: 71, delta: -1, direction: 'down' },
};

/**
 * Returns mock Network NPS score.
 * Pass a brand name to get brand-specific data.
 * In production, this would come from a customer satisfaction/survey endpoint.
 */
export const getMockNetworkNPS = (brandName?: string): { score: number; delta: number; direction: 'up' | 'down' | 'stable' } =>
  getMockValueForBrand(brandName, BRAND_NPS, { score: 72, delta: 2, direction: 'up' });

const BRAND_ROYALTY_DELTA: Record<MockBrandKey, { direction: 'up' | 'down' | 'stable' }> = {
  AutoFix:  { direction: 'down' },
  CleanCo:  { direction: 'up' },
  LawnPros: { direction: 'stable' },
  PetCare:  { direction: 'stable' },
};

/**
 * Returns mock Royalty Integrity delta.
 * Pass a brand name to get brand-specific data.
 */
export const getMockRoyaltyIntegrityDelta = (brandName?: string): { direction: 'up' | 'down' | 'stable' } =>
  getMockValueForBrand(brandName, BRAND_ROYALTY_DELTA, { direction: 'stable' });

// ─── Zone 2: Performance & Growth ───────────────────────────────

/**
 * Returns 12-month AUV trend data
 * The last month's value will be overridden with real data in the component
 */
export const getMockAUVTrend = (): AUVTrendPoint[] => {
  const months = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'];
  const values = [
    142000, 148000, 151000, 155000, 159000, 162000,
    158000, 165000, 170000, 173000, 178000, 181000,
  ];

  return months.map((month, i) => ({
    month,
    auv: values[i],
    target: 175000,
  }));
};

/**
 * Returns mock "Time to First Dollar" onboarding data
 * Shows last 5 franchisees with days from signing to first revenue job
 * Names represent territory locations (e.g., "CleanCo - Westlake")
 * In production, this would track actual onboarding milestones
 */
export const getMockOnboardingData = (): OnboardingEntry[] => [
  { franchisee: 'CleanCo Westlake', days: 18, target: 21, brandColor: '#0066CC' },
  { franchisee: 'LawnPros Katy', days: 24, target: 21, brandColor: '#2E7D32' },
  { franchisee: 'AutoFix Plano', days: 15, target: 21, brandColor: '#D32F2F' },
  { franchisee: 'PetCare Frisco', days: 32, target: 21, brandColor: '#7B1FA2' },
  { franchisee: 'CleanCo Midtown', days: 19, target: 21, brandColor: '#0066CC' },
];

// ─── Zone 3: Compliance & SOP Health ────────────────────────────

const BRAND_LEAD_FUNNEL: Record<MockBrandKey, LeadFunnelStage[]> = {
  AutoFix: [
    { stage: 'Leads Received', count: 94, color: '#1976D2' },
    { stage: 'Quotes Sent', count: 61, color: '#F57C00' },
    { stage: 'Jobs Converted', count: 38, color: '#2E7D32' },
  ],
  CleanCo: [
    { stage: 'Leads Received', count: 112, color: '#1976D2' },
    { stage: 'Quotes Sent', count: 74, color: '#F57C00' },
    { stage: 'Jobs Converted', count: 52, color: '#2E7D32' },
  ],
  LawnPros: [
    { stage: 'Leads Received', count: 86, color: '#1976D2' },
    { stage: 'Quotes Sent', count: 50, color: '#F57C00' },
    { stage: 'Jobs Converted', count: 32, color: '#2E7D32' },
  ],
  PetCare: [
    { stage: 'Leads Received', count: 50, color: '#1976D2' },
    { stage: 'Quotes Sent', count: 28, color: '#F57C00' },
    { stage: 'Jobs Converted', count: 18, color: '#2E7D32' },
  ],
};

/**
 * Returns mock Lead Conversion Funnel data.
 * Pass a brand name to get brand-specific data.
 * In production, this would come from a leads/CRM endpoint.
 */
export const getMockLeadFunnelData = (brandName?: string): LeadFunnelStage[] => {
  return getMockValueForBrand(brandName, BRAND_LEAD_FUNNEL, [
    { stage: 'Leads Received', count: 342, color: '#1976D2' },
    { stage: 'Quotes Sent', count: 198, color: '#F57C00' },
    { stage: 'Jobs Converted', count: 127, color: '#2E7D32' },
  ]);
};

// ─── Zone 4: Financial Governance ───────────────────────────────

const BRAND_LEDGER_SYNC: Record<MockBrandKey, LedgerSyncData> = {
  AutoFix: {
    syncPercentage: 91,
    lastSyncTime: new Date(Date.now() - 120 * 60 * 1000).toISOString(),
    totalUnits: 11,
    syncedUnits: 10,
    flaggedUnits: [
      { name: 'AutoFix Plano', reason: 'Invoice mismatch - 3 transactions pending review', lastSync: '2 hours ago' },
    ],
  },
  CleanCo: {
    syncPercentage: 100,
    lastSyncTime: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    totalUnits: 12,
    syncedUnits: 12,
    flaggedUnits: [],
  },
  LawnPros: {
    syncPercentage: 100,
    lastSyncTime: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
    totalUnits: 10,
    syncedUnits: 10,
    flaggedUnits: [],
  },
  PetCare: {
    syncPercentage: 100,
    lastSyncTime: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    totalUnits: 7,
    syncedUnits: 7,
    flaggedUnits: [],
  },
};

/**
 * Returns mock Ledger Sync status.
 * Pass a brand name to get brand-specific data with correct sync percentages.
 * In production, this would come from an ERP/accounting integration status endpoint.
 */
export const getMockLedgerSyncData = (brandName?: string): LedgerSyncData => {
  return getMockValueForBrand(brandName, BRAND_LEDGER_SYNC, {
    syncPercentage: 98,
    lastSyncTime: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
    totalUnits: 40,
    syncedUnits: 39,
    flaggedUnits: [
      { name: 'AutoFix Plano', reason: 'Invoice mismatch - 3 transactions pending review', lastSync: '2 hours ago' },
    ],
  });
};

/**
 * Returns mock AR Aging data
 * In production, this would come from royalty/fee payment tracking
 */
export const getMockARAgingData = (): ARAgingEntry[] => [
  { franchisee: 'AutoFix Plano', amountDue: 4250, daysOverdue: 67, bucket: '60+', type: 'Royalty Fee' },
  { franchisee: 'PetCare Frisco', amountDue: 1800, daysOverdue: 45, bucket: '30-60', type: 'Tech Fee' },
  { franchisee: 'LawnPros Katy', amountDue: 3100, daysOverdue: 38, bucket: '30-60', type: 'Royalty Fee' },
  { franchisee: 'CleanCo Cypress', amountDue: 950, daysOverdue: 31, bucket: '30-60', type: 'Marketing Fund' },
  { franchisee: 'AutoFix Sugar Land', amountDue: 5600, daysOverdue: 92, bucket: '90+', type: 'Royalty Fee' },
];
